import { DateTime } from 'luxon'
import { ComposerTranslation } from 'vue-i18n'
import { RestaurantWeeklyDaysOrder, RestaurantWeeklyDaysUnion } from '~/lib/interfaces/restaurant'
import { McDiaComponent, McDiaMessageStatus, McDiaMessageStatusType } from '~/lib/models/mcdia'
import { AdvanceSalesMessages } from '~/lib/services/store/country/country.dtos'

export function transformDateToLocaleDate({
  date,
  utc = false,
  format = 'dd/MM/y',
}: {
  date: string
  utc?: boolean
  format?: string
}) {
  return DateTime.fromISO(date, { zone: utc ? 'utc' : undefined }).toFormat(format)
}

export function transformDateToLocaleTime(date: string, utc: boolean = false) {
  return DateTime.fromISO(date, { zone: utc ? 'utc' : undefined }).toFormat('HH:mm')
}

/**
 * Check date and format it to
 * Today - HH:MM, Yesterday - HH:MM or DD/MM - HH:MM
 *
 * @param string
 * @returns string
 */
export function transformOrderDate(date: string) {
  const { t } = useLocale()
  const orderDate = new Date(date)
  const actualDay = new Date()
  const yesterday = new Date()
  yesterday.setDate(actualDay.getDate() - 1)

  const timeTwoDigit = (number: number) => ('0' + number).slice(-2)
  const hour = timeTwoDigit(orderDate.getHours())
  const minutes = timeTwoDigit(orderDate.getMinutes())

  const isToday = actualDay.toDateString() === orderDate.toDateString()
  const isYesterday = yesterday.toDateString() === orderDate.toDateString()

  if (isToday) {
    return `${t('orders.date.today')} - ${hour}:${minutes}`
  } else if (isYesterday) {
    return `${t('orders.date.yesterday')} - ${hour}:${minutes}`
  } else {
    return `${orderDate.toLocaleDateString()} - ${hour}:${minutes}`
  }
}

/**
 * Transform ISO string date to local date (overriding zone to local)
 * 1995-12-06T00:00:00.000Z => Wed Dec 06 1995 00:00:00 GMT+0100 (hora estándar de Europa central)
 * @param ISOdate: string
 */
export function ISODateOverrideZoneLocal(ISOdate: string): Date {
  const utc = DateTime.fromISO(ISOdate, { zone: 'utc' })
  const { day, month, year } = utc
  const dt = DateTime.fromObject(
    { day, month, year, hour: 0, minute: 0, second: 0, millisecond: 0 },
    { zone: 'local' }
  )
  return dt.toJSDate()
}

/**
 * Transform local date to UTC DateTime (overriding zone to utc)
 * Wed Dec 06 1995 00:00:00 GMT+0100 (hora estándar de Europa central) => 1995-12-06T00:00:00.000Z
 * We need this format to send the date to the backend
 * @param date: Date
 */
export function localDateOverrideZoneUTC(date: Date): DateTime {
  const dt = DateTime.fromJSDate(date, { zone: 'local' })

  const { day, month, year } = dt
  const dtBuilded = DateTime.fromObject(
    { day, month, year, hour: 0, minute: 0, second: 0, millisecond: 0 },
    { zone: 'utc' }
  )

  return dtBuilded
}

/**
 * Check if the user is over minAge years old
 * @param date
 * @returns boolean
 */
export function checkAge(date: Date, minAge: string) {
  const minimumAge = parseInt(minAge)
  if (Number.isNaN(minimumAge) || minimumAge <= 0) return true
  const today = new Date()
  const birthDate = new Date(date)
  let age = today.getFullYear() - birthDate.getFullYear()
  const month = today.getMonth() - birthDate.getMonth()

  if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  if (age < minimumAge) return false

  return true
}

export const getMcDiaMessageStatus = (mcdiaDates: {
  dateFrom: string
  dateTo: string
}): McDiaMessageStatusType => {
  const { dateFrom, dateTo } = mcdiaDates
  // check date, is before, 3 days before, or is after
  const now = DateTime.utc()

  const dateFromDatetime = DateTime.fromISO(dateFrom, { zone: 'utc' })
  const dateToDatetime = DateTime.fromISO(dateTo, { zone: 'utc' })
  const todayResetTime = now.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
  const diffInDaysToStart = dateFromDatetime.diff(todayResetTime, 'days').days

  if (diffInDaysToStart > 0 && diffInDaysToStart <= 3) {
    return McDiaMessageStatus.THREE_DAYS_BEFORE
  }

  if (
    todayResetTime.toMillis() >= dateFromDatetime.toMillis() &&
    todayResetTime.toMillis() <= dateToDatetime.toMillis()
  )
    return McDiaMessageStatus.ON_DATE
  if (todayResetTime.toMillis() > dateToDatetime.toMillis()) {
    return McDiaMessageStatus.AFTER_DATE
  }
  return McDiaMessageStatus.ADVANCE_SALE
}

export const getMcDiaMessageComponent = ({
  component = McDiaComponent.CART,
  messages,
  mcdiaDates,
}: {
  component: string
  messages: AdvanceSalesMessages
  mcdiaDates: { dateFrom: string; dateTo: string }
}): string => {
  const status = getMcDiaMessageStatus(mcdiaDates)

  const isAdvanceSale = status === McDiaMessageStatus.ADVANCE_SALE
  const isOnDate = status === McDiaMessageStatus.ON_DATE

  let message = ''
  switch (component) {
    case McDiaComponent.CART: {
      const cartMessages = messages.cart
      if (isOnDate) message = cartMessages.inDate
      else if (isAdvanceSale) message = cartMessages.beforeDate
      break
    }
    case McDiaComponent.ORDER_DETAIL: {
      const orderDetailMessages = messages.orderDetail
      if (isOnDate) message = orderDetailMessages.inDate
      else if (isAdvanceSale) message = orderDetailMessages.beforeDate
      else message = orderDetailMessages.outDate
      break
    }
    case McDiaComponent.PAYMENT: {
      const paymentMessages = messages.payment
      if (isOnDate) message = paymentMessages.inDate
      else if (isAdvanceSale) message = paymentMessages.beforeDate
      break
    }
  }

  const messageWithBreaklines = message.replace(/\s{2}/gm, '<br/>')
  return messageWithBreaklines
}

/* Countdown */
export const formatCountdown = (
  { hours, minutes, seconds }: { hours: number; minutes: number; seconds: number },
  t: ComposerTranslation
) => {
  if (hours) {
    const isManyHours = hours > 1
    const unitHours = isManyHours ? t('common.time.hours') : t('common.time.hour')
    if (minutes > 0) {
      const unitMinutes = minutes > 1 ? t('common.time.minutes') : t('common.time.minute')
      return `${hours} ${unitHours} ${t('common.and')} ${minutes} ${unitMinutes}`
    }
    return `${hours} ${unitHours}`
  } else if (minutes) {
    const isMany = minutes > 1
    const unit = isMany ? t('common.time.minutes') : t('common.time.minute')
    if (!isMany) {
      const unitSeconds = seconds > 1 ? t('common.time.seconds') : t('common.time.second')
      return `${minutes} ${unit} ${t('common.and')} ${seconds} ${unitSeconds}`
    }
    return `${minutes} ${unit}`
  }
  const isMany = seconds > 1
  const unit = isMany ? t('common.time.seconds') : t('common.time.second')
  return `${seconds} ${unit}`
}

const units: Intl.RelativeTimeFormatUnit[] = [
  'year',
  'month',
  'week',
  'day',
  'hour',
  'minute',
  'second',
]

export const timeAgoFromDate = (dateTime: DateTime, lang: string, countryCode: string) => {
  const mainLang = lang.split('_')[0]
  const locale = `${mainLang}-${countryCode}`
  const diff = dateTime.diffNow().shiftTo(...units)
  const unit = units.find(unit => diff.get(unit) !== 0) || 'second'
  const relativeFormatter = new Intl.RelativeTimeFormat(locale, {
    numeric: 'auto',
  })
  return relativeFormatter.format(Math.trunc(diff.as(unit)), unit)
}

export const getIntlUnitsFromDate = (
  dateTime: DateTime,
  unitsArr: Intl.RelativeTimeFormatUnit[] = units
) => {
  const diff = dateTime
    .diffNow()
    .shiftTo(...unitsArr)
    .toObject()

  return diff
}

export const getWeeklyDay = (dateTime: DateTime): RestaurantWeeklyDaysUnion => {
  const day = dateTime.weekday
  return RestaurantWeeklyDaysOrder[day - 1]
}

/**
 * Input mask for a day/month/year date (for inputs that support digit masks).
 * Pattern: "dd/mm/yyyy".
 */
export const MASK_DMY = '99/99/9999'

/**
 * Strict regular expression that validates the *shape* "dd/mm/yyyy".
 * It is syntax-only: it does not verify calendar validity (e.g., 31/04).
 * Calendar validation is performed by `parseDMYMasked`.
 */
export const REGEX_DMY = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/

/**
 * Formats a Date as "dd/mm/yyyy" using the local time zone.
 * Does not mutate the provided Date instance.
 * @param date - Date to format.
 * @returns Formatted string (e.g., "02/09/2025").
 */
export const formatDMY = (date: Date): string =>
  DateTime.fromJSDate(date, { zone: 'local' }).toFormat('dd/MM/yyyy')

/**
 * Returns a new Date set to local midnight (00:00:00.000) for the given date.
 * @param date - Date to normalize.
 * @returns New Date instance at local midnight.
 */
export const dateAtMidnight = (date: Date): Date =>
  DateTime.fromJSDate(date, { zone: 'local' }).startOf('day').toJSDate()

/**
 * Returns today's date at local midnight (00:00:00.000) in the current time zone.
 * @returns New Date instance representing today at local midnight.
 */
export const todayMidnight = (): Date => DateTime.local().startOf('day').toJSDate()

/**
 * Parses a strict "dd/mm/yyyy" string into a real Date (local midnight).
 * Success conditions:
 *  - The string matches `REGEX_DMY` (syntactic shape).
 *  - The parsed value is a real calendar date (e.g., rejects 31/02, bad leap days).
 *
 * NOTE: This function does NOT enforce range checks (e.g., “not in the future”).
 *       Perform those validations externally if needed.
 *
 * @param inputDate - Date string in "dd/mm/yyyy" format.
 * @returns A valid Date at local midnight, or `null` if the input is invalid.
 */
export function parseDMYMasked(inputDate: string): Date | null {
  const value = inputDate.trim()
  if (!REGEX_DMY.test(value)) return null

  const dt = DateTime.fromFormat(value, 'dd/MM/yyyy', { zone: 'local' })
  return dt.isValid ? dt.startOf('day').toJSDate() : null
}
